/**
 * Carousel Storage Service
 * 
 * Handles localStorage persistence for carousel completion status.
 * Carousel only appears once per device - completion is stored locally.
 */

const CAROUSEL_STORAGE_KEY = 'streamko_carousel_completed';
const CAROUSEL_COMPLETED_AT_KEY = 'streamko_carousel_completed_at';

export interface CarouselStorageState {
  completed: boolean;
  completedAt: string | null;
}

/**
 * Check if carousel has been completed
 */
export function isCarouselCompleted(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    const value = localStorage.getItem(CAROUSEL_STORAGE_KEY);
    return value === 'true';
  } catch {
    // localStorage not available (e.g., private browsing)
    return false;
  }
}

/**
 * Mark carousel as completed
 */
export function markCarouselCompleted(): void {
  if (typeof window === 'undefined') return;
  
  try {
    const now = new Date().toISOString();
    localStorage.setItem(CAROUSEL_STORAGE_KEY, 'true');
    localStorage.setItem(CAROUSEL_COMPLETED_AT_KEY, now);
  } catch {
    // localStorage not available
    console.warn('Unable to save carousel completion to localStorage');
  }
}

/**
 * Get full carousel storage state
 */
export function getCarouselStorageState(): CarouselStorageState {
  if (typeof window === 'undefined') {
    return { completed: false, completedAt: null };
  }
  
  try {
    const completed = localStorage.getItem(CAROUSEL_STORAGE_KEY) === 'true';
    const completedAt = localStorage.getItem(CAROUSEL_COMPLETED_AT_KEY);
    return { completed, completedAt };
  } catch {
    return { completed: false, completedAt: null };
  }
}

/**
 * Reset carousel completion (for testing/development)
 */
export function resetCarouselCompletion(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(CAROUSEL_STORAGE_KEY);
    localStorage.removeItem(CAROUSEL_COMPLETED_AT_KEY);
  } catch {
    // localStorage not available
  }
}

/**
 * Sync carousel completion to backend after user signup
 * This is called after authentication to persist the carousel state to the user's preferences
 */
export async function syncCarouselCompletionToBackend(apiUrl: string, token: string): Promise<void> {
  const state = getCarouselStorageState();
  
  if (!state.completed) return;
  
  try {
    await fetch(`${apiUrl}/api/onboarding/carousel/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        completedAt: state.completedAt,
      }),
    });
  } catch (error) {
    console.error('Failed to sync carousel completion to backend:', error);
  }
}

export default {
  isCarouselCompleted,
  markCarouselCompleted,
  getCarouselStorageState,
  resetCarouselCompletion,
  syncCarouselCompletionToBackend,
};
